import '../../i18n/autoTranslate';
import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const QuestionConfiguration = ({
  embedded = false,
  configuration,
  onConfigurationChange,
  className = '',
}) => {
  const [customQuestions, setCustomQuestions] = useState(
    configuration?.customQuestions || ['']
  );

  const handleToggleChange = (key, value) => {
    onConfigurationChange({
      ...configuration,
      [key]: value,
    });
  };

  const handleAddQuestion = () => {
    setCustomQuestions([...customQuestions, '']);
  };

  const handleQuestionChange = (index, value) => {
    const updated = [...customQuestions];
    updated[index] = value;
    setCustomQuestions(updated);

    onConfigurationChange({
      ...configuration,
      customQuestions: updated,
    });
  };

  const handleRemoveQuestion = (index) => {
    const updated = customQuestions.filter((_, i) => i !== index);
    setCustomQuestions(updated);

    onConfigurationChange({
      ...configuration,
      customQuestions: updated,
    });
  };

  const panel =
    'relative overflow-hidden rounded-3xl border border-white/10 bg-[#0b0f16]/60 backdrop-blur-xl shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_18px_60px_-40px_rgba(56,189,248,0.18)]';

  const panelHeadIcon =
    'flex items-center justify-center w-9 h-9 rounded-2xl border border-white/10 bg-gradient-to-br from-sky-500/15 via-violet-500/10 to-emerald-500/10 shadow-[0_0_22px_rgba(56,189,248,0.20)]';

  return (
    <div className={`p-0 transition-all duration-200 ease-smooth space-y-6 ${className}`}>
      {!embedded && (
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center gap-3 mb-2">
            <div className="flex items-center justify-center w-10 h-10 rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl">
              <img src="/question_yagabm.png" alt="questions" className="w-10 h-10" />
            </div>
            <h2 className="text-2xl font-semibold tracking-tight text-white">
              Question Configuration
            </h2>
          </div>
          <p className="text-white/60">
            Choose your question sources and customize the interview content.
          </p>
        </div>
      )}

      <div className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Custom Questions */}
          <div className={`${panel} p-6`}
          >
            <div className="pointer-events-none absolute inset-0 opacity-70 [background:radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.08),transparent_60%)]" />

            <div className="relative flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className={panelHeadIcon}>
                  <Icon name="Edit3" size={16} className="text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Custom Questions</h3>
                  <p className="text-sm text-white/60">
                    Add your own prompts to practice specific scenarios.
                  </p>
                </div>
              </div>
              <Button onClick={handleAddQuestion} variant="default" iconName="Plus" size="sm">
                Add
              </Button>
            </div>

            <div className="relative mt-5 space-y-3">
              {customQuestions.map((question, index) => (
                <div
                  key={index}
                  className="group flex items-start gap-3 rounded-2xl p-3 border border-white/10 bg-white/[0.03] backdrop-blur-xl hover:border-white/16 transition-all duration-200"
                >
                  <textarea
                    value={question}
                    onChange={(e) => handleQuestionChange(index, e.target.value)}
                    placeholder={`Enter custom interview question ${index + 1}...`}
                    className="w-full h-20 p-2 bg-transparent text-white placeholder-white/35 resize-none focus:outline-none focus:ring-2 focus:ring-white/10 focus:border-transparent rounded-xl"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Trash2"
                    className="text-rose-300 hover:text-rose-200 hover:bg-rose-500/10"
                    onClick={() => handleRemoveQuestion(index)}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Flow + Difficulty */}
          <div className="space-y-6">
            <div className={`${panel} p-6`}>
              <div className="pointer-events-none absolute inset-0 opacity-70 [background:radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.08),transparent_60%)]" />
              <div className="relative flex items-center gap-3">
                <div className={panelHeadIcon}>
                  <Icon name="GitBranch" size={16} className="text-white" />
                </div>
                <h3 className="text-lg font-semibold text-white">Question Flow</h3>
              </div>

              <div className="relative mt-4 space-y-3">
                <Checkbox
                  label="Enable Follow-up Questions"
                  description="AI will ask follow-up questions based on your answers"
                  checked={configuration?.enableFollowUps || false}
                  onChange={(e) => handleToggleChange('enableFollowUps', e?.target?.checked)}
                />

                <Checkbox
                  label="Surprise Questions"
                  description="Include unexpected questions to test adaptability"
                  checked={configuration?.surpriseQuestions || false}
                  onChange={(e) => handleToggleChange('surpriseQuestions', e?.target?.checked)}
                />

                <Checkbox
                  label="STAR Method Enforcement"
                  description="AI will guide you to use STAR method for behavioral questions"
                  checked={configuration?.starMethodEnforcement || false}
                  onChange={(e) => handleToggleChange('starMethodEnforcement', e?.target?.checked)}
                />
              </div>
            </div>

            <div className={`${panel} p-6`}>
              <div className="pointer-events-none absolute inset-0 opacity-70 [background:radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.08),transparent_60%)]" />

              <div className="relative flex items-center gap-3">
                <div className={panelHeadIcon}>
                  <Icon name="TrendingUp" size={16} className="text-white" />
                </div>
                <h3 className="text-lg font-semibold text-white">Difficulty</h3>
              </div>

              <div className="relative mt-4 space-y-3">
                <div>
                  <label className="text-sm font-medium text-white mb-2 block">
                    Follow-up Intensity
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="5"
                    value={configuration?.followUpIntensity || 3}
                    onChange={(e) =>
                      handleToggleChange('followUpIntensity', parseInt(e?.target?.value))
                    }
                    className="w-full h-2 rounded-lg appearance-none cursor-pointer bg-white/10"
                    style={{
                      background: `linear-gradient(to right, #38BDF8 0%, #38BDF8 ${((
                        (configuration?.followUpIntensity || 3) - 1
                      ) * 25)}%, rgba(255,255,255,0.12) ${((
                        (configuration?.followUpIntensity || 3) - 1
                      ) * 25)}%, rgba(255,255,255,0.12) 100%)`,
                    }}
                  />
                  <div className="flex justify-between text-xs text-white/45 mt-1">
                    <span>Light</span>
                    <span>Moderate</span>
                    <span>Intense</span>
                  </div>
                </div>

                <Checkbox
                  label="Adaptive Difficulty"
                  description="AI adjusts question difficulty based on your performance"
                  checked={configuration?.adaptiveDifficulty || false}
                  onChange={(e) => handleToggleChange('adaptiveDifficulty', e?.target?.checked)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="pt-6 border-t border-white/10">
        <div className={`${panel} p-5`}>
          <div className="pointer-events-none absolute inset-0 opacity-60 [background:radial-gradient(circle_at_20%_20%,rgba(16,185,129,0.16),transparent_55%)]" />

          <div className="relative flex items-start gap-3">
            <div className={panelHeadIcon}>
              <Icon name="Info" size={16} className="text-white" />
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-medium text-white mb-2">
                Configuration Summary
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-white/60">
                <div className="flex items-start gap-2">
                  <Icon name="BookOpen" size={12} className="text-white mt-0.5 flex-shrink-0" />
                  <span>
                    Categories: {configuration?.selectedCategories?.length || 0} selected
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <Icon name="Edit3" size={12} className="text-white mt-0.5 flex-shrink-0" />
                  <span>
                    Custom Questions: {customQuestions?.filter((q) => q.trim()).length || 0}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuestionConfiguration;
